import { useEffect, useRef, useState } from "react";
import { FiBell, FiTrash2, FiExternalLink } from "react-icons/fi";
import styles from "./NotificationCenter.module.css";
import useNotificationStore from "../../store/notificationStore";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

// Matches the project's convention (see SessionCard.jsx):
// backend returns UTC; we explicitly parse as UTC then convert to local.
const formatLocalTime = (iso) => {
  if (!iso) return "";
  return dayjs.utc(iso).local().format("DD-MM-YYYY HH:mm");
};

const NotificationCenter = () => {
  const {
    notifications,
    unreadCount,
    isLoading,
    isOpen,
    setIsOpen,
    fetchUnreadCount,
    fetchNotifications,
    markRead,
    markAllRead,
    deleteNotification,
  } = useNotificationStore();

  const popupRef = useRef(null);
  const bellRef = useRef(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Heuristic: body is likely truncated if it's long or has line breaks.
  const isBodyLikelyTruncated = (text) => {
    if (!text) return false;
    return text.length > 140 || (text.match(/\n/g) || []).length >= 2;
  };

  // The title is single-line + ellipsis, so anything that doesn't fit
  // comfortably on one row counts as truncated for the toggle.
  const isTitleLikelyTruncated = (text) => {
    if (!text) return false;
    return text.length > 40;
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60_000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  useEffect(() => {
    const onClick = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        bellRef.current &&
        !bellRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [isOpen, setIsOpen]);

  const handleItemClick = async (n) => {
    if (!n.is_read) await markRead(n.id);
    if (n.link_url) {
      window.open(n.link_url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className={styles.wrapper}>
      <button
        ref={bellRef}
        className={styles.bellBtn}
        onClick={() => setIsOpen(!isOpen)}
        title="Notifications"
        aria-label="Notifications"
      >
        <FiBell />
        {unreadCount > 0 && (
          <span className={styles.badge}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className={styles.popup} ref={popupRef}>
          <div className={styles.header}>
            <strong>Notifications</strong>
            {notifications.some((n) => !n.is_read) && (
              <button className={styles.markAllBtn} onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className={styles.list}>
            {isLoading ? (
              <div className={styles.empty}>Loading…</div>
            ) : notifications.length === 0 ? (
              <div className={styles.empty}>You're all caught up</div>
            ) : (
              notifications.map((n) => {
                const isExpanded = expandedIds.has(n.id);
                const hasSubBody = !!(n.sub_body && n.sub_body.trim());
                const showToggle =
                  hasSubBody ||
                  isBodyLikelyTruncated(n.body) ||
                  isTitleLikelyTruncated(n.title);
                return (
                  <div
                    key={n.id}
                    className={`${styles.item} ${
                      n.is_read ? "" : styles.unread
                    }`}
                    onClick={() => handleItemClick(n)}
                  >
                    {!n.is_read && <span className={styles.dot} />}
                    <div className={styles.content}>
                      <div className={styles.titleRow}>
                        <span
                          className={`${styles.title} ${
                            isExpanded ? styles.titleExpanded : ""
                          }`}
                        >
                          {n.title}
                        </span>
                        <span className={styles.time}>{formatLocalTime(n.sent_at)}</span>
                      </div>
                      <p
                        className={`${styles.body} ${
                          isExpanded ? "" : styles.bodyCollapsed
                        }`}
                      >
                        {n.body}
                      </p>
                      {hasSubBody && isExpanded && (
                        <p className={styles.subBody}>{n.sub_body}</p>
                      )}
                      {showToggle && (
                        <button
                          type="button"
                          className={styles.toggleBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(n.id);
                          }}
                        >
                          {isExpanded ? "Show less" : "Show more"}
                        </button>
                      )}
                      {n.link_url && (
                        <span className={styles.linkHint}>
                          <FiExternalLink /> Tap to open
                        </span>
                      )}
                    </div>
                    <button
                      className={styles.deleteBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(n.id);
                      }}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
